import React, {useEffect} from "react";
import {AccountingAccount, AccountingAccountList, AccountingAccountRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {Grid, Typography} from "@mui/material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {types, TypeConf} from "../../../../components/form/types";
import {nextSubaccountCode} from "../../../../components/utils/accounts";

export const AccountingAccountEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(AccountingAccountRestControllerApiFactory);

    const form = useForm({
        fetcher: (id) => ctrl.getAccountingAccount(id),
        updater: (bean) => ctrl.saveAccountingAccount(bean),
        remover: (id) => ctrl.deleteOrDismissAccountingAccount(id),
        factory: () => ({} as AccountingAccount),
    } as UseFormProps<AccountingAccount>);

    const handleParentChange = (_type: TypeConf<AccountingAccount>, parent: AccountingAccount | null) => {
        if (!parent?.id || form.hasId()) return;
        apiManager.execute(ctrl.listAccountingAccount(undefined, undefined, parent.id))
            .then((children: AccountingAccountList[]) => {
                // Formato de Contasol (#14623): la siguiente subcuenta de 8 dígitos del padre (626 → 62600001).
                // Antes proponía el padre más «0» (6260) o el máximo de los hijos más uno, mezclando longitudes.
                const nextCode = nextSubaccountCode(parent.code, (children ?? []).map(c => c.code));
                if (nextCode) form.triggerField("code", nextCode);
            });
    };

    return (
        <FormContent form={form} title={"Formulario de cuenta contable"}>

            <FormAutocomplete
                field={"parent"}
                label={"Cuenta padre"}
                type={types.accountingAccounts}
                helpText={"Cuenta de nivel superior en la jerarquía. Selecciónala primero: se sugerirá automáticamente el siguiente código disponible."}
                onChange={handleParentChange}
            />

            <Grid container spacing={3}>
                <Grid item sm={3}>
                    <FormInput
                        field={"code"}
                        label={"Código"}
                        helperText={"Código numérico único (ej. 4300001). Se calcula automáticamente al elegir la cuenta padre; modifícalo si necesitas otro valor."}
                    />
                </Grid>
                <Grid item sm={9}>
                    <FormInput
                        field={"name"}
                        label={"Nombre"}
                        helperText={"Denominación oficial de la cuenta según el Plan General Contable."}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item sm={4}>
                    <FormSelect
                        field={"accountType"}
                        label={"Tipo"}
                        type={types.accountingAccountTypes}
                        helperText={"ACTIVO/PASIVO → balance; INGRESO/GASTO → PyG; NETO → patrimonio neto."}
                    />
                </Grid>
                <Grid item sm={4}>
                    <FormSelect
                        field={"nature"}
                        label={"Naturaleza"}
                        type={types.accountingNatures}
                        helperText={"DEUDORA: saldo habitual en el Debe (activos, gastos). ACREEDORA: saldo habitual en el Haber (pasivos, ingresos)."}
                    />
                </Grid>
                <Grid item sm={4}>
                    <FormToggle field={"allowsMovements"} label={"Permite movimientos"}/>
                    <Typography variant="caption" color="text.secondary" display="block" mt={-1}>
                        Actívalo solo en cuentas de detalle (subcuentas) donde se registran asientos contables.
                    </Typography>
                </Grid>
            </Grid>

            <FormInput field={"description"} label={"Descripción"} rows={3}/>
        </FormContent>
    );
};
